/* eslint-disable react/no-array-index-key */
import { CSSProperties, ReactNode, useCallback, useRef } from 'react';
import { Button, Grid2, Stack } from '@mui/material';
import { Form, Formik, FormikHelpers, FormikValues } from 'formik';
import { PropsOf } from '@emotion/react';
import ValuesListener from '../../form/ValuesListener';
import TextInput from '../../form/base/TextInput';
import SingleSelect from '../../form/base/SingleSelect';
import MultiSelect from '../../form/base/MultiSelect';
import { isNil } from '../../../utils/global/helper';
import BirthdayInput from '../../form/base/BirthdayInput';
import ColoredAlert, { ColoredAlertProps } from '../ColoredAlert';
import TextareaInput from '../../form/base/TextareaInput';
import SmallModal, { Footer, FooterProps } from '../SmallModal';
import CheckboxInput from '../../form/base/CheckboxInput';
import { FormikForm } from '../../../interfaces';
import FileInput from '../../form/base/FileInput';
import CategorySingleSelect from '../../form/base/CategorySingleSelect';
import MultiCheckboxInput from '../../form/base/MultiCheckboxInput';
import RadioSelect from '../../form/base/RadioSelect';
import MultiSwitchInput from '../../form/base/MultiSwitchInput';
import DualSlidersInput from '../../form/base/DualSlidersInput';
import RichTextEditor from '../../form/base/RichTextInput';
import Copy from '../../form/base/Copy';
import SwitchInput from '../../form/base/SwitchInput';

type ActionButtonInput = {
  inputType: 'action-button';
  label: string;
  onClick: () => void;
  isVisible?: boolean | ((values: FormikValues) => boolean);
  disabled?: boolean;
};

type NodeInput = {
  inputType: 'node';
  node: ReactNode;
  isVisible?: boolean | ((values: FormikValues) => boolean);
  disabled?: boolean;
};

type FormInput = {
  isVisible?: boolean | ((values: FormikValues) => boolean);
} & (
  | ({
      inputType: 'text' | 'number' | 'date-time';
    } & PropsOf<typeof TextInput>)
  | ({
      inputType: 'select';
    } & PropsOf<typeof SingleSelect>)
  | ({
      inputType: 'category-select';
    } & PropsOf<typeof CategorySingleSelect>)
  | ({
      inputType: 'textarea';
    } & PropsOf<typeof TextareaInput>)
  | ({
      inputType: 'multiSelect';
    } & PropsOf<typeof MultiSelect>)
  | ({
      inputType: 'birthday';
    } & PropsOf<typeof BirthdayInput>)
  | ({
      inputType: 'checkbox';
    } & PropsOf<typeof CheckboxInput>)
  | ({
      inputType: 'file';
    } & PropsOf<typeof FileInput>)
  | ({
      inputType: 'multi-checkbox';
    } & PropsOf<typeof MultiCheckboxInput>)
  | ({
      inputType: 'radio-select';
    } & PropsOf<typeof RadioSelect>)
  | ({
      inputType: 'multi-switch';
    } & PropsOf<typeof MultiSwitchInput>)
  | ({
      inputType: 'dual-sliders';
    } & PropsOf<typeof DualSlidersInput>)
  | ({
      inputType: 'richtext';
    } & PropsOf<typeof RichTextEditor>)
  | ({
      inputType: 'copy';
    } & PropsOf<typeof Copy>)
  | ({
      inputType: 'switch';
    } & PropsOf<typeof SwitchInput>)
);

type Input = FormInput | ActionButtonInput | NodeInput;

export type InputRow<Values extends FormikValues> = (
  | (FormInput & {
      name: keyof Values | (keyof Values)[];
      required?: boolean;
    })
  | ActionButtonInput
  | NodeInput
)[];

export type FormData<Values extends FormikValues = any> = FormikForm<Values> & {
  title?: string;
  truncateTitle?: boolean;
  submitButtonText?: string;
  destructiveAction?: FooterProps['destructiveAction'];
  inputRows:
    | InputRow<Values>[]
    | ((values: Values, helpers: FormikHelpers<Values>) => InputRow<Values>[]);
};

const formStyle: CSSProperties = {
  width: '100%',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

interface Props {
  open: boolean;
  alert?:
    | ColoredAlertProps
    | ((values: FormikValues) => ColoredAlertProps | null);
  onClose: () => void;
  onValuesChange?: (values: FormikValues) => void;
  form: FormData | null;
  maxWidth?: CSSProperties['width'];
  allowUntouched?: boolean;
  submitOnClose?: boolean;
  children?: ReactNode;
}

function FormSmallModal({
  open,
  onClose,
  onValuesChange,
  form,
  alert,
  maxWidth,
  allowUntouched = false,
  submitOnClose = false,
  children,
}: Props) {
  const isInputVisible = (
    input: Pick<Input, 'isVisible'>,
    values: FormikValues,
  ) => {
    const value =
      isNil(input.isVisible) ||
      (typeof input.isVisible === 'function'
        ? input.isVisible(values)
        : input.isVisible);
    return value;
  };

  const submitFormRef = useRef<FormikHelpers<FormikValues>['submitForm']>();
  const closeModal = useCallback(async () => {
    if (submitOnClose && submitFormRef.current) {
      await submitFormRef.current();
    }
    onClose();
  }, [onClose, submitOnClose]);

  if (!form) return null;

  return (
    <SmallModal
      open={open}
      onClose={closeModal}
      title={form.title}
      truncateTitle={form.truncateTitle}
      width={maxWidth}>
      {form && (
        <Formik
          enableReinitialize
          validateOnMount
          validateOnChange
          initialValues={form.initialValues}
          validationSchema={form.validationSchema}
          onSubmit={async (values, helpers) => {
            try {
              helpers.setSubmitting(true);
              await form.onSubmit(values, helpers);
            } catch {
              // Nothing
            } finally {
              helpers.setSubmitting(false);
            }
          }}>
          {helpers => {
            const { isSubmitting, isValid, isValidating, dirty, values } =
              helpers;
            const inputRows =
              typeof form.inputRows === 'function'
                ? form.inputRows(values, helpers)
                : form.inputRows;
            const alertProps = alert
              ? typeof alert === 'function'
                ? alert(values)
                : alert
              : null;
            submitFormRef.current = helpers.submitForm;
            return (
              <Form style={formStyle}>
                {onValuesChange && (
                  <ValuesListener onChange={onValuesChange} debounceTime={0} />
                )}
                <Stack
                  p={2}
                  pb={submitOnClose ? '32px' : undefined}
                  width="100%"
                  gap={2}
                  boxSizing="border-box">
                  {alertProps && <ColoredAlert {...alertProps} />}

                  {inputRows.map((inputRow, index) => {
                    const isAnyInputVisible = inputRow.some(input =>
                      isInputVisible(input, values),
                    );
                    if (!isAnyInputVisible) return null;
                    return (
                      <Grid2
                        container
                        columns={{
                          xs: 1,
                          sm: inputRow.length,
                        }}
                        gap={2}
                        flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                        key={index}
                        maxWidth="100%"
                        width="100%"
                        boxSizing="border-box">
                        {inputRow.map(({ inputType, ...input }, inputIndex) => {
                          const isVisible = isInputVisible(input, values);
                          if (!isVisible) return null;

                          if (
                            inputType === 'text' ||
                            inputType === 'number' ||
                            inputType === 'date-time'
                          ) {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <TextInput
                                  type={
                                    inputType === 'date-time'
                                      ? 'datetime-local'
                                      : inputType
                                  }
                                  {...(input as PropsOf<typeof TextInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'textarea') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <TextareaInput
                                  {...(input as PropsOf<typeof TextareaInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'select') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <SingleSelect
                                  {...(input as PropsOf<typeof SingleSelect>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'multiSelect') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <MultiSelect
                                  {...(input as PropsOf<typeof MultiSelect>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'birthday') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <BirthdayInput
                                  {...(input as PropsOf<typeof BirthdayInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'checkbox') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <CheckboxInput
                                  {...(input as PropsOf<typeof CheckboxInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'file') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <FileInput
                                  {...(input as PropsOf<typeof FileInput>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'category-select') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <CategorySingleSelect
                                  {...(input as PropsOf<
                                    typeof CategorySingleSelect
                                  >)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'multi-checkbox') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <MultiCheckboxInput
                                  {...(input as PropsOf<
                                    typeof MultiCheckboxInput
                                  >)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'radio-select') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <RadioSelect
                                  {...(input as PropsOf<typeof RadioSelect>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'multi-switch') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <MultiSwitchInput
                                  {...(input as PropsOf<
                                    typeof MultiSwitchInput
                                  >)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'dual-sliders') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <DualSlidersInput
                                  {...(input as PropsOf<
                                    typeof DualSlidersInput
                                  >)}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'richtext') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <RichTextEditor
                                  {...(input as PropsOf<typeof RichTextEditor>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'copy') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <Copy
                                  {...(input as PropsOf<typeof Copy>)}
                                  disabled={input.disabled || isSubmitting}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'switch') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <SwitchInput
                                  {...(input as PropsOf<typeof SwitchInput>)}
                                />
                              </Grid2>
                            );
                          }
                          if (inputType === 'action-button') {
                            const { label, onClick } =
                              input as ActionButtonInput;
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                <Button
                                  variant="light"
                                  color="info"
                                  fullWidth
                                  onClick={onClick}>
                                  {label}
                                </Button>
                              </Grid2>
                            );
                          }
                          if (inputType === 'node') {
                            return (
                              <Grid2 key={inputIndex} size={1} width="100%">
                                {(input as NodeInput).node}
                              </Grid2>
                            );
                          }
                          return null;
                        })}
                      </Grid2>
                    );
                  })}
                  {children}
                </Stack>
                {!submitOnClose && (
                  <Footer
                    button={{
                      text: form.submitButtonText ?? 'Update',
                      disabled:
                        isSubmitting ||
                        !isValid ||
                        isValidating ||
                        (!allowUntouched && !dirty),
                      loading: isSubmitting,
                      type: 'submit',
                    }}
                    destructiveAction={
                      form.destructiveAction && {
                        label: form.destructiveAction.label,
                        // Removing is not submitting, so this closes directly.
                        onClick: () => {
                          form.destructiveAction?.onClick();
                          onClose();
                        },
                      }
                    }
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </SmallModal>
  );
}

export default FormSmallModal;
